import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowRight, Mail, Phone, MapPin, Globe, Upload, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { INQUIRY_TYPES, PREFERRED_MODES, COUNTRIES } from "@/lib/data";

import API from "@/lib/api";

const EMPTY = {
  full_name: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  inquiry_type: "",
  summary: "",
  preferred_mode: "",
  document_name: "",
};

const FALLBACK = {
  hero: {
    overline: "Contact",
    title_pre: "Let's Build Your ",
    title_highlight: "Next-Generation Technology System",
    paragraph1: "Have a complex business challenge, enterprise technology requirement, AI initiative, digital twin idea, simulation project, or strategic partnership opportunity? NGXP Technologies is ready to discuss how we can help.",
    paragraph2: "Share your requirement with us. Our team will understand your objective, review the opportunity, and guide you toward the right technology direction.",
    cta_label: "Start a Strategic Discussion",
  },
  conversation: {
    overline: "Get in Touch",
    title_pre: "Start the Conversation with ",
    title_highlight: "NGXP",
    lead: "Every strong technology solution begins with the right discussion.",
    paragraph: "Whether you are planning a new enterprise platform, upgrading existing systems, exploring AI capabilities, building a digital twin, creating immersive training, or evaluating a long-term technology partner, NGXP can help you understand what is possible and how to move forward.",
    audience_label: "We welcome conversations with:",
    audiences: [
      "Enterprise clients", "Business leaders", "Operations teams", "Technology heads",
      "Innovation teams", "Investors", "Strategic partners", "Global organizations",
      "Startups with complex product ideas", "Industry leaders exploring next-generation systems",
    ],
  },
  "how-we-help": {
    overline: "Scope",
    title_pre: "How We Can ",
    title_highlight: "Help",
    paragraph: "You can contact NGXP Technologies for any of the following:",
    items: [
      "AI-native enterprise platforms", "Agentic AI and AI assistant systems",
      "RAG, Graph RAG, and knowledge intelligence systems", "Digital twin and real-time monitoring systems",
      "Computer vision and visual intelligence systems", "IoT, embedded, and edge intelligence solutions",
      "AI + 3D simulation systems", "Immersive 3D, AR, VR, MR, and XR training systems",
      "Command centers and decision intelligence dashboards", "Intelligent workflow automation",
      "Enterprise system modernization", "Strategic technology partnerships",
      "Investment, collaboration, or joint venture discussions",
    ],
  },
  form: {
    heading: "Tell Us About Your Requirement",
    subheading: "Please share a few details so we can understand your need and respond with the right direction.",
    success_title: "Thank you for reaching out.",
    success_message: "Our team has received your inquiry and will respond within one business day.",
    inquiry_types: INQUIRY_TYPES,
    preferred_modes: PREFERRED_MODES,
  },
  "contact-info": {
    company: "NGXP Technologies",
    intro: "Reach our team through any channel below.",
    email: "info@ngxptechnologies.com",
    phone: "+91 9941630848, +91 9962647558",
    website: "www.ngxptechnologies.com",
    website_url: "https://www.ngxptechnologies.com",
    offices: [
      {
        name: "Kochi Office",
        address: "BB Arcade, Millumpadi Road, Nest Road, Near NEST Techpark, Kakkanad, Kochi, Kerala, India 682042",
        map_url: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3929.0!2d76.3416!3d10.0262!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b080d8e0c0f0001%3A0x0!2sKakkanad%2C+Kochi%2C+Kerala+682042!5e0!3m2!1sen!2sin!4v1",
      },
      {
        name: "Kollam Office",
        address: "Technopark Kollam, SBC No: 5, 1st Floor, Ashtamudi Building, Kundara, Kollam, Kerala, India 691501",
        map_url: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3941.0!2d76.5762!3d8.8932!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b05e9e0c0f0001%3A0x0!2sTechnopark+Kollam%2C+Kundara%2C+Kollam%2C+Kerala+691501!5e0!3m2!1sen!2sin!4v1",
      },
    ],
  },
  "strategic-partner": {
    overline: "Strategic Partnership",
    title_pre: "Looking for a ",
    title_highlight: "Strategic Technology Partner?",
    paragraph1: "If your requirement is still at an idea, concept, or early planning stage, you can still contact us.",
    paragraph2: "Many advanced systems begin with a business problem, not a ready-made specification. NGXP can help you structure the requirement, identify the right technology approach, and define a practical roadmap before development begins.",
    cta_label: "Book a Discussion",
    items: [
      "We can help clarify the problem",
      "We can identify the right technology direction",
      "We can suggest a practical execution roadmap",
      "We can help convert ideas into structured requirements",
      "We can support proof-of-concept planning",
      "We can explore long-term partnership possibilities",
    ],
  },
  "best-fit": {
    overline: "Ideal Clients",
    title_pre: "Who We Work ",
    title_highlight: "Best With",
    paragraph: "NGXP is best suited for organizations that want to solve meaningful business or operational challenges using advanced technology.",
    items_label: "You should contact us if you are looking to:",
    items: [
      "Build a serious enterprise-grade technology system",
      "Improve visibility across operations, teams, or assets",
      "Use AI to reduce manual effort and improve decisions",
      "Modernize existing enterprise platforms",
      "Create a real-time monitoring or dashboard system",
      "Develop a digital twin or simulation-based solution",
      "Improve training through immersive 3D or XR experiences",
      "Connect field activity, sensors, or operational data into digital systems",
      "Build a scalable solution with long-term business value",
      "Explore a strategic technology partnership",
    ],
  },
  "global-reach": {
    overline: "Global Reach",
    title_pre: "Open to Global Projects, ",
    title_highlight: "Partnerships, and Collaboration",
    paragraph1: "NGXP Technologies works with organizations that are serious about building future-ready systems. We are open to discussions with clients and partners across India, the Middle East, Europe, the United States, and global markets.",
    paragraph2: "Whether you are a growing enterprise, established industry leader, technology company, investor, or strategic partner, we welcome meaningful conversations around advanced technology collaboration.",
  },
  "what-happens": {
    overline: "Our Process",
    title_pre: "What Happens After ",
    title_highlight: "You Contact Us",
    steps: [
      { num: "01", title: "We Review Your Requirement", desc: "Our team reviews your message, project summary, business context, and any supporting documents shared." },
      { num: "02", title: "We Understand the Objective", desc: "We identify the core problem, expected outcome, business priority, and possible technology direction." },
      { num: "03", title: "We Schedule a Discussion", desc: "If the requirement matches NGXP's capabilities, we schedule a discussion to understand the opportunity in detail." },
      { num: "04", title: "We Recommend the Next Step", desc: "Based on the discussion, we may suggest a discovery session, technical consultation, POC plan, proposal, roadmap, or partnership conversation." },
    ],
  },
  "final-cta": {
    title: "Have a Complex Technology Challenge? Let's Discuss It.",
    paragraph: "Tell us what you are trying to build, improve, monitor, automate, simulate, or transform. NGXP Technologies can help you identify the right path forward.",
    cta_primary_label: "Submit Your Requirement",
    cta_secondary_label: "Start a Strategic Discussion",
    tagline: "NGXP Technologies – Building future-ready systems for organizations ready to move beyond ordinary technology.",
  },
};

function buildLookup(sections) {
  const map = {};
  for (const s of sections) map[s.key] = s;
  const get = (key, field, fallback) => {
    const s = map[key];
    if (!s) return fallback;
    const v = s.content?.[field];
    if (v === undefined || v === null) return fallback;
    if (typeof v === "string" && v.trim() === "") return fallback;
    if (Array.isArray(v) && v.length === 0) return fallback;
    return v;
  };
  const enabled = (key) => {
    if (Object.keys(map).length === 0) return true;
    return !!map[key]?.enabled;
  };
  return { get, enabled };
}

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sections, setSections] = useState([]);
  const { hash } = useLocation();

  useEffect(() => {
    axios.get(`${API}/pages/contact/sections`).then(({ data }) => {
      if (Array.isArray(data)) setSections(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (hash === "#contact-form") {
      setTimeout(() => {
        const el = document.getElementById("contact-form");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [hash]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onChange("document_name", file.name);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.summary || !form.inquiry_type || !form.preferred_mode) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry submitted successfully. Our team will be in touch.");
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldBase =
    "w-full rounded-xl bg-[#F8F9FA] border border-gray-200 px-4 py-3 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#7B2CBF] focus:border-transparent transition-all";

  const { get, enabled } = buildLookup(sections);
  const F = FALLBACK;

  const hero = {
    o: get("hero", "overline", F.hero.overline),
    pre: get("hero", "title_pre", F.hero.title_pre),
    hl: get("hero", "title_highlight", F.hero.title_highlight),
    p1: get("hero", "paragraph1", F.hero.paragraph1),
    p2: get("hero", "paragraph2", F.hero.paragraph2),
    cta: get("hero", "cta_label", F.hero.cta_label),
  };
  const conv = {
    o: get("conversation", "overline", F.conversation.overline),
    pre: get("conversation", "title_pre", F.conversation.title_pre),
    hl: get("conversation", "title_highlight", F.conversation.title_highlight),
    lead: get("conversation", "lead", F.conversation.lead),
    p: get("conversation", "paragraph", F.conversation.paragraph),
    label: get("conversation", "audience_label", F.conversation.audience_label),
    audiences: get("conversation", "audiences", F.conversation.audiences),
  };
  const help = {
    o: get("how-we-help", "overline", F["how-we-help"].overline),
    pre: get("how-we-help", "title_pre", F["how-we-help"].title_pre),
    hl: get("how-we-help", "title_highlight", F["how-we-help"].title_highlight),
    p: get("how-we-help", "paragraph", F["how-we-help"].paragraph),
    items: get("how-we-help", "items", F["how-we-help"].items),
  };
  const fm = {
    heading: get("form", "heading", F.form.heading),
    sub: get("form", "subheading", F.form.subheading),
    successT: get("form", "success_title", F.form.success_title),
    successM: get("form", "success_message", F.form.success_message),
    inquiries: get("form", "inquiry_types", F.form.inquiry_types),
    modes: get("form", "preferred_modes", F.form.preferred_modes),
  };
  const ci = {
    company: get("contact-info", "company", F["contact-info"].company),
    intro: get("contact-info", "intro", F["contact-info"].intro),
    email: get("contact-info", "email", F["contact-info"].email),
    phone: get("contact-info", "phone", F["contact-info"].phone),
    website: get("contact-info", "website", F["contact-info"].website),
    websiteUrl: get("contact-info", "website_url", F["contact-info"].website_url),
    offices: get("contact-info", "offices", F["contact-info"].offices),
  };
  const sp = {
    o: get("strategic-partner", "overline", F["strategic-partner"].overline),
    pre: get("strategic-partner", "title_pre", F["strategic-partner"].title_pre),
    hl: get("strategic-partner", "title_highlight", F["strategic-partner"].title_highlight),
    p1: get("strategic-partner", "paragraph1", F["strategic-partner"].paragraph1),
    p2: get("strategic-partner", "paragraph2", F["strategic-partner"].paragraph2),
    cta: get("strategic-partner", "cta_label", F["strategic-partner"].cta_label),
    items: get("strategic-partner", "items", F["strategic-partner"].items),
  };
  const bf = {
    o: get("best-fit", "overline", F["best-fit"].overline),
    pre: get("best-fit", "title_pre", F["best-fit"].title_pre),
    hl: get("best-fit", "title_highlight", F["best-fit"].title_highlight),
    p: get("best-fit", "paragraph", F["best-fit"].paragraph),
    label: get("best-fit", "items_label", F["best-fit"].items_label),
    items: get("best-fit", "items", F["best-fit"].items),
  };
  const gr = {
    o: get("global-reach", "overline", F["global-reach"].overline),
    pre: get("global-reach", "title_pre", F["global-reach"].title_pre),
    hl: get("global-reach", "title_highlight", F["global-reach"].title_highlight),
    p1: get("global-reach", "paragraph1", F["global-reach"].paragraph1),
    p2: get("global-reach", "paragraph2", F["global-reach"].paragraph2),
  };
  const wh = {
    o: get("what-happens", "overline", F["what-happens"].overline),
    pre: get("what-happens", "title_pre", F["what-happens"].title_pre),
    hl: get("what-happens", "title_highlight", F["what-happens"].title_highlight),
    steps: get("what-happens", "steps", F["what-happens"].steps),
  };
  const cta = {
    title: get("final-cta", "title", F["final-cta"].title),
    p: get("final-cta", "paragraph", F["final-cta"].paragraph),
    cpl: get("final-cta", "cta_primary_label", F["final-cta"].cta_primary_label),
    csl: get("final-cta", "cta_secondary_label", F["final-cta"].cta_secondary_label),
    tag: get("final-cta", "tagline", F["final-cta"].tagline),
  };

  return (
    <div data-testid="contact-page">
      {enabled("hero") && (
      <section className="relative bg-white pt-6 md:pt-10 pb-10 md:pb-14 overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="glow-blob bg-[#7B2CBF]/20 w-[500px] h-[500px] -top-32 -right-32 pulse-glow" />
        <div className="container-ngxp relative z-10 max-w-3xl">
          <span className="overline">{hero.o}</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0A0B10]">
            {hero.pre}<span className="gradient-text-glow">{hero.hl}</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">{hero.p1}</p>
          <p className="mt-4 text-base text-gray-500 leading-relaxed">{hero.p2}</p>
          <div className="mt-8">
            <a href="#contact-form" className="btn-primary-gradient inline-flex" data-testid="contact-hero-cta">
              {hero.cta} <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
      )}

      {enabled("conversation") && (
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6 reveal">
              <span className="overline">{conv.o}</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
                {conv.pre}<span className="gradient-text-glow">{conv.hl}</span>
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed font-medium">{conv.lead}</p>
              <p className="mt-4 text-gray-600 leading-relaxed">{conv.p}</p>
            </div>
            <div className="lg:col-span-6 reveal">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">{conv.label}</p>
              <div className="grid grid-cols-2 gap-3">
                {conv.audiences.map((item, i) => (
                  <div key={i} className="contact-item flex items-center gap-2" style={{ animationDelay: `${i * 0.07}s` }}>
                    <CheckCircle2 className="contact-icon w-4 h-4 text-[#7B2CBF] shrink-0" />
                    <span className="contact-text text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("how-we-help") && (
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-2xl reveal">
            <span className="overline">{help.o}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              {help.pre}<span className="gradient-text-glow">{help.hl}</span>
            </h2>
            <p className="mt-4 text-gray-600">{help.p}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 reveal">
            {help.items.map((item, i) => (
              <div
                key={i}
                className="group flex items-start gap-3 bg-white rounded-xl border border-gray-200 p-4 transition-all duration-300 hover:border-[#00A8E8] hover:shadow-lg hover:-translate-y-1 hover:bg-gradient-to-br hover:from-white hover:to-[#e8f7fd]"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <ArrowRight className="w-4 h-4 text-[#00A8E8] shrink-0 mt-0.5 transition-transform duration-300 group-hover:translate-x-1" />
                <span className="text-sm text-gray-700 leading-relaxed group-hover:text-[#0A0B10] transition-colors duration-300">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {(enabled("form") || enabled("contact-info")) && (
      <section id="contact-form" className="bg-white py-12 md:py-16">
        <div className="container-ngxp grid grid-cols-1 lg:grid-cols-12 gap-8">
          {enabled("form") && (
          <div className="lg:col-span-7 ngxp-card !p-8 md:!p-10">
            {submitted ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00A8E8] to-[#7B2CBF] items-center justify-center text-white mb-5">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display text-2xl font-bold">{fm.successT}</h3>
                <p className="mt-3 text-gray-600 max-w-md mx-auto">{fm.successM}</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 btn-secondary-light" data-testid="contact-submit-another">Submit another inquiry</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <h3 className="font-display text-2xl font-bold text-[#0A0B10]">{fm.heading}</h3>
                  <p className="text-sm text-gray-500 mt-1">{fm.sub}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Full Name *">
                    <input className={fieldBase} value={form.full_name} onChange={(e) => onChange("full_name", e.target.value)} required data-testid="contact-input-name" />
                  </Field>
                  <Field label="Company / Organization Name">
                    <input className={fieldBase} value={form.company} onChange={(e) => onChange("company", e.target.value)} data-testid="contact-input-company" />
                  </Field>
                  <Field label="Business Email *">
                    <input type="email" className={fieldBase} value={form.email} onChange={(e) => onChange("email", e.target.value)} required data-testid="contact-input-email" />
                  </Field>
                  <Field label="Phone / WhatsApp">
                    <input className={fieldBase} value={form.phone} onChange={(e) => onChange("phone", e.target.value)} data-testid="contact-input-phone" />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Country / Region">
                    <select className={fieldBase} value={form.country} onChange={(e) => onChange("country", e.target.value)} data-testid="contact-select-country">
                      <option value="">Select country...</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Type of Inquiry *">
                    <select className={fieldBase} value={form.inquiry_type} onChange={(e) => onChange("inquiry_type", e.target.value)} required data-testid="contact-select-inquiry">
                      <option value="">Select type...</option>
                      {fm.inquiries.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>

                <Field label="Project / Requirement Summary *">
                  <textarea
                    className={`${fieldBase} min-h-[140px] resize-y`}
                    value={form.summary}
                    onChange={(e) => onChange("summary", e.target.value)}
                    placeholder="Describe your operational challenge, system idea, or partnership interest..."
                    required
                    data-testid="contact-textarea-summary"
                  />
                </Field>

                <Field label="Preferred Mode of Discussion *">
                  <div className="flex flex-wrap gap-2">
                    {fm.modes.map((m) => (
                      <button
                        type="button"
                        key={m}
                        onClick={() => onChange("preferred_mode", m)}
                        data-testid={`contact-mode-${m.split(" ")[0].toLowerCase()}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                          form.preferred_mode === m
                            ? "bg-[#0A0B10] text-white border-[#0A0B10]"
                            : "bg-white text-gray-700 border-gray-200 hover:border-[#7B2CBF]"
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Upload Supporting Document (optional)">
                  <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-dashed border-gray-300 cursor-pointer hover:border-[#7B2CBF] transition-colors" data-testid="contact-upload-label">
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{form.document_name || "Click to attach a file (PDF, DOC, image)"}</span>
                    <input type="file" className="hidden" onChange={onFile} data-testid="contact-upload-input" />
                  </label>
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary-gradient w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                  {loading ? "Submitting..." : "Submit Inquiry"}
                </button>
              </form>
            )}
          </div>
          )}

          {enabled("contact-info") && (
          <div className="lg:col-span-5 space-y-6">
            <div className="ngxp-card !bg-[#0A0B10] !text-white !border-white/10 relative overflow-hidden">
              <div className="absolute inset-0 dark-grid-pattern opacity-40" />
              <div className="relative z-10">
                <h3 className="font-display text-2xl font-bold">{ci.company}</h3>
                <p className="text-sm text-white/60 mt-2">{ci.intro}</p>

                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex gap-3">
                    <Mail className="w-4 h-4 mt-1 text-[#00A8E8] shrink-0" />
                    <a href={`mailto:${ci.email}`} className="hover:text-white text-white/80" data-testid="contact-info-email">{ci.email}</a>
                  </li>
                  <li className="flex gap-3">
                    <Phone className="w-4 h-4 mt-1 text-[#7B2CBF] shrink-0" />
                    <span className="text-white/80" data-testid="contact-info-phone">{ci.phone}</span>
                  </li>
                  <li className="flex gap-3">
                    <Globe className="w-4 h-4 mt-1 text-[#FF007F] shrink-0" />
                    <a href={ci.websiteUrl} target="_blank" rel="noreferrer" className="hover:text-white text-white/80">{ci.website}</a>
                  </li>
                </ul>
              </div>
            </div>

            {ci.offices.map((office, i) => (
              <div key={i} className="ngxp-card !p-0 overflow-hidden">
                <div className="flex gap-3 p-5 border-b border-gray-100">
                  <MapPin className={`w-5 h-5 shrink-0 mt-0.5 ${i % 2 === 0 ? "text-[#7B2CBF]" : "text-[#FF007F]"}`} />
                  <div>
                    <h4 className="font-display font-semibold">{office.name}</h4>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{office.address}</p>
                  </div>
                </div>
                <iframe
                  title={`NGXP ${office.name}`}
                  src={office.map_url}
                  width="100%"
                  height="200"
                  style={{ border: 0, display: "block" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid={`map-${office.name.split(" ")[0].toLowerCase()}`}
                />
              </div>
            ))}
          </div>
          )}
        </div>
      </section>
      )}

      {enabled("strategic-partner") && (
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-pattern opacity-30" />
        <div className="glow-blob bg-[#7B2CBF]/20 w-[500px] h-[500px] -top-32 -right-20 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6 reveal">
              <span className="overline">{sp.o}</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
                {sp.pre}<span className="gradient-text-glow">{sp.hl}</span>
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">{sp.p1}</p>
              <p className="mt-4 text-white/70 leading-relaxed">{sp.p2}</p>
              <div className="mt-8">
                <a href="#contact-form" className="btn-primary-gradient inline-flex" data-testid="contact-strategic-cta">
                  {sp.cta} <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-6 reveal">
              <ul className="space-y-4 mt-4">
                {sp.items.map((item, i) => (
                  <li key={i} className="partner-item flex items-start gap-3" style={{ animationDelay: `${i * 0.09}s` }}>
                    <CheckCircle2 className="partner-icon w-5 h-5 text-[#00A8E8] shrink-0 mt-0.5" />
                    <span className="partner-text text-white/80 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("best-fit") && (
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <span className="overline">{bf.o}</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
                {bf.pre}<span className="gradient-text-glow">{bf.hl}</span>
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">{bf.p}</p>
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">{bf.label}</p>
              <ul className="space-y-3">
                {bf.items.map((item, i) => (
                  <li key={i} className="choose-item flex items-start gap-3" style={{ animationDelay: `${i * 0.07}s` }}>
                    <CheckCircle2 className="choose-icon w-5 h-5 text-[#7B2CBF] shrink-0 mt-0.5" />
                    <span className="choose-text text-gray-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("global-reach") && (
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-3xl mx-auto text-center reveal">
            <span className="overline">{gr.o}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              {gr.pre}<span className="gradient-text-glow">{gr.hl}</span>
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">{gr.p1}</p>
            <p className="mt-4 text-gray-600 leading-relaxed">{gr.p2}</p>
          </div>
        </div>
      </section>
      )}

      {enabled("what-happens") && (
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-2xl reveal">
            <span className="overline">{wh.o}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              {wh.pre}<span className="gradient-text-glow">{wh.hl}</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 reveal">
            {wh.steps.map((step, i) => (
              <div key={i} className="ngxp-card reveal">
                <span className="text-3xl font-display font-bold gradient-text-glow">{step.num}</span>
                <h3 className="font-display text-lg font-semibold text-[#0A0B10] mt-3">{step.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {enabled("final-cta") && (
      <section className="bg-white py-14 md:py-18">
        <div className="container-ngxp">
          <div className="rounded-3xl bg-gradient-to-br from-[#00A8E8] via-[#7B2CBF] to-[#FF007F] p-10 md:p-16 text-white relative overflow-hidden">
            <div className="absolute inset-0 dark-grid-pattern opacity-30" />
            <div className="relative z-10">
              <div className="max-w-3xl">
                <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{cta.title}</h3>
                <p className="mt-4 text-white/85 leading-relaxed max-w-2xl">{cta.p}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#contact-form" data-testid="contact-final-cta-form" className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0B10] px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                  {cta.cpl} <ArrowUpRight className="w-4 h-4" />
                </a>
                <a href="#contact-form" data-testid="contact-final-cta-discuss" className="inline-flex items-center gap-2 rounded-full bg-white/20 border border-white/30 text-white px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                  {cta.csl} <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <p className="mt-8 text-white/50 text-xs text-center">{cta.tag}</p>
            </div>
          </div>
        </div>
      </section>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 mb-2">{label}</span>
      {children}
    </label>
  );
}
